import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';
import { getDDQSections, DDQSection, DDQQuestion } from '../data/ddqQuestions';

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  real_estate: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

export default function DDQWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { firm } = useProfile();

  const ddqType = (searchParams.get('type') || 'firm') as 'firm' | 'fund';
  const assetClass = searchParams.get('asset_class') || 'hedge_fund';
  const fundId = searchParams.get('fund_id') || undefined;
  const existingDdqId = searchParams.get('ddq_id') || undefined;

  const [ddqId, setDdqId] = useState<string | null>(existingDdqId || null);
  const ddqIdRef = useRef<string | null>(existingDdqId || null);
  const [sections, setSections] = useState<DDQSection[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Record<string, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'draft' | 'submitted'>('draft');
  const [allowPdf, setAllowPdf] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const s = getDDQSections(assetClass, ddqType);
    setSections(s);
  }, [assetClass, ddqType]);

  useEffect(() => {
    if (existingDdqId) loadExistingDDQ(existingDdqId);
    else if (firm) initDDQ();
  }, [firm, existingDdqId]);

  const initDDQ = async () => {
    if (!firm) return;
    const { data } = await supabase.from('ddqs').insert({
      firm_id: firm.id,
      fund_id: fundId || null,
      type: ddqType,
      asset_class: assetClass,
      status: 'draft',
      allow_pdf: false,
    }).select().single();
    if (data) {
      setDdqId(data.id);
      ddqIdRef.current = data.id;
    }
  };

  const loadExistingDDQ = async (id: string) => {
    const { data: ddq } = await supabase.from('ddqs').select('*').eq('id', id).single();
    if (ddq) {
      setStatus(ddq.status);
      setAllowPdf(ddq.allow_pdf);
      ddqIdRef.current = id;
    }
    const { data: answerData } = await supabase
      .from('ddq_answers').select('*').eq('ddq_id', id);
    if (answerData) {
      const grouped: Record<string, Record<string, string>> = {};
      answerData.forEach((a: any) => {
        if (!grouped[a.section_key]) grouped[a.section_key] = {};
        grouped[a.section_key][a.question_key] = a.answer_text || '';
      });
      setAnswers(grouped);
    }
  };

  const saveAnswer = async (sectionKey: string, questionKey: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [questionKey]: value }
    }));
    const currentDdqId = ddqId || ddqIdRef.current;
    if (!currentDdqId) return;
    await supabase.from('ddq_answers').upsert({
      ddq_id: currentDdqId,
      section_key: sectionKey,
      question_key: questionKey,
      answer_text: value,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'ddq_id,section_key,question_key' });
  };

  const handleSave = async () => {
    setSaving(true);
    const currentDdqId = ddqId || ddqIdRef.current;
    if (currentDdqId) {
      await supabase.from('ddqs').update({ updated_at: new Date().toISOString() }).eq('id', currentDdqId);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setSaving(false);
  };

  const handleSubmit = async () => {
    const currentDdqId = ddqId || ddqIdRef.current;
    if (!currentDdqId) return;
    setSubmitting(true);
    await supabase.from('ddqs').update({
      status: 'submitted', allow_pdf: allowPdf, updated_at: new Date().toISOString()
    }).eq('id', currentDdqId);
    const uid = (await supabase.auth.getUser()).data.user?.id;
    if (uid && firm) logActivity(uid, firm.id, 'ddq_submitted', 'ddq', currentDdqId, { ddq_type: ddqType, asset_class: assetClass });
    setStatus('submitted');
    setSubmitting(false);
    navigate('/manage-ddq');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const currentDdqId = ddqIdRef.current || ddqId;
    if (!file) return;
    if (!currentDdqId) {
      setAiMessage('DDQ not ready. Please close this page, go back to DDQ Manager and try again.');
      return;
    }
    setUploading(true);
    setAiMessage('Reading your DDQ...');

    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = (ev.target?.result as string).split(',')[1];
        setAiMessage('Extracting answers with AI...');
        setAiProcessing(true);

        const allQuestions = sections.flatMap(s =>
          s.questions.map(q => `Section: ${s.title}\nQuestion key: ${s.key}.${q.key}\nQuestion: ${q.label}`)
        ).join('\n\n');

        const { data: aiData, error: aiError } = await supabase.functions.invoke('altveno-ai', {
          body: {
            systemPrompt: 'You are helping pre-fill a DDQ form. Extract answers from the uploaded document and map them to the provided questions. Return ONLY a valid JSON object where keys are "section_key.question_key" and values are the extracted answer text. If no answer found for a question, omit it. Keep answers concise and factual. No preamble, no markdown, just the JSON object.',
            messages: [{
              role: 'user',
              content: [
                {
                  type: 'document',
                  source: {
                    type: 'base64',
                    media_type: file.type === 'application/pdf' ? 'application/pdf' : 'text/plain',
                    data: base64
                  }
                },
                {
                  type: 'text',
                  text: `Extract answers from the document above and map to these questions:\n\n${allQuestions}`
                }
              ]
            }],
          },
        });

        if (aiError) {
          console.error('AI error:', aiError);
          setAiMessage('AI error: ' + JSON.stringify(aiError));
          setAiProcessing(false);
          setUploading(false);
          return;
        }

        console.log('AI response:', JSON.stringify(aiData));
        const text = aiData?.content?.[0]?.text || '';
        console.log('Text:', text);

        try {
          const clean = text.replace(/```json|```/g, '').trim();
          const parsed = JSON.parse(clean);

          const newAnswers: Record<string, Record<string, string>> = { ...answers };
          let count = 0;

          for (const [key, value] of Object.entries(parsed)) {
            const [sectionKey, ...questionParts] = key.split('.');
            const questionKey = questionParts.join('.');
            if (!newAnswers[sectionKey]) newAnswers[sectionKey] = {};
            newAnswers[sectionKey][questionKey] = value as string;

            await supabase.from('ddq_answers').upsert({
              ddq_id: currentDdqId,
              section_key: sectionKey,
              question_key: questionKey,
              answer_text: value as string,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'ddq_id,section_key,question_key' });
            count++;
          }

          setAnswers(newAnswers);
          setAiMessage(`✓ Pre-filled ${count} answers from your document. Please review and complete any gaps.`);
        } catch (parseErr) {
          console.error('Parse error:', parseErr, 'Raw:', text);
          setAiMessage('Could not parse AI response. Please fill in manually.');
        }

        setAiProcessing(false);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      console.error('Upload error:', err);
      setAiMessage('Upload failed: ' + (err?.message || String(err)));
      setUploading(false);
      setAiProcessing(false);
    }
  };

  const isLastStep = currentStep === sections.length;
  const currentSection = sections[currentStep];
  const progress = Math.round((currentStep / (sections.length)) * 100);

  const inputStyle = {
    width: '100%', background: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: '3px', color: '#0A0A0A',
    fontSize: '13px', padding: '10px 14px',
    outline: 'none', fontFamily: 'system-ui, -apple-system, sans-serif',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    fontSize: '13px', color: '#3D4552',
    marginBottom: '6px', display: 'block'
  };

  const renderQuestion = (section: DDQSection, q: DDQQuestion) => {
    const value = answers[section.key]?.[q.key] || '';
    const onChange = (val: string) => saveAnswer(section.key, q.key, val);

    return (
      <div key={q.key} style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>
          {q.label}
          {q.required && <span style={{ color: '#C0222A', marginLeft: '4px' }}>*</span>}
        </label>
        {q.type === 'textarea' ? (
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={q.placeholder}
            rows={4}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
          />
        ) : q.type === 'select' ? (
          <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
            <option value="">Select...</option>
            {q.options?.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : q.type === 'boolean' ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            {['Yes', 'No'].map(opt => (
              <button
                key={opt}
                onClick={() => onChange(opt)}
                style={{
                  padding: '8px 24px', borderRadius: '3px', fontSize: '13px',
                  cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                  border: `1px solid ${value === opt ? '#C9A84C' : '#D0D5DD'}`,
                  background: value === opt ? '#FDF6E7' : 'transparent',
                  color: value === opt ? '#0A1628' : '#6B7280',
                }}
              >{opt}</button>
            ))}
          </div>
        ) : q.type === 'multiselect' ? (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {q.options?.map(opt => {
              const selected = value.split(',').map(v => v.trim()).includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => {
                    const current = value ? value.split(',').map(v => v.trim()).filter(Boolean) : [];
                    const next = selected ? current.filter(v => v !== opt) : [...current, opt];
                    onChange(next.join(', '));
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: '20px', fontSize: '12px',
                    cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                    border: `1px solid ${selected ? '#C9A84C' : '#D0D5DD'}`,
                    background: selected ? '#FDF6E7' : 'transparent',
                    color: selected ? '#0A1628' : '#6B7280',
                  }}
                >{opt}</button>
              );
            })}
          </div>
        ) : (
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={q.placeholder}
            style={inputStyle}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '28px', background: '#F5F6F8', minHeight: '100vh' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <button onClick={() => navigate('/manage-ddq')} style={{
            background: '#FFFFFF', border: '1px solid #D0D5DD',
            borderRadius: '3px', color: '#3D4552', padding: '6px 12px',
            cursor: 'pointer', fontSize: '12px', fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>← Back</button>
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: '#0A0A0A', marginBottom: '2px' }}>
              {ddqType === 'firm' ? 'Firm DDQ' : 'Fund DDQ'}, {classLabels[assetClass] || assetClass}
            </h1>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              {status === 'submitted' ? '✓ Submitted' : 'Draft · Auto-saved'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            {status !== 'submitted' && (
              <button onClick={handleSave} disabled={saving} style={{
                background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '3px', color: saved ? '#0A7C42' : '#3D4552',
                padding: '7px 14px', cursor: 'pointer', fontSize: '12px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>{saved ? '✓ Saved' : 'Save draft'}</button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>
              {isLastStep ? 'Review & Submit' : `Step ${currentStep + 1} of ${sections.length}, ${currentSection?.title}`}
            </span>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>{progress}%</span>
          </div>
          <div style={{ height: '4px', background: '#E8EBF0', borderRadius: '2px' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: '#0A1628', borderRadius: '2px', transition: 'width 0.3s' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap' }}>
            {sections.map((s, i) => (
              <button key={s.key} onClick={() => setCurrentStep(i)} style={{
                padding: '3px 10px', borderRadius: '20px', fontSize: '10.5px',
                cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
                border: `1px solid ${i === currentStep ? '#0A1628' : i < currentStep ? '#0A7C42' : '#D0D5DD'}`,
                background: i === currentStep ? '#0A1628' : i < currentStep ? '#0A7C4215' : '#F5F6F8',
                color: i === currentStep ? '#FFFFFF' : i < currentStep ? '#0A7C42' : '#6B7280',
              }}>{s.title}</button>
            ))}
            <button onClick={() => setCurrentStep(sections.length)} style={{
              padding: '3px 10px', borderRadius: '20px', fontSize: '10.5px',
              cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
              border: `1px solid ${isLastStep ? '#0A1628' : '#D0D5DD'}`,
              background: isLastStep ? '#0A1628' : '#F5F6F8',
              color: isLastStep ? '#FFFFFF' : '#6B7280',
            }}>Review</button>
          </div>
        </div>

        {/* AI upload */}
        {currentStep === 0 && !existingDdqId && (
          <div style={{
            background: '#FFFFFF', border: '1px solid #D0D5DD',
            borderRadius: '4px', padding: '20px', marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ fontSize: '16px' }}>✦</span>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#0A0A0A' }}>Have an existing DDQ?</div>
            </div>
            <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, marginBottom: '14px' }}>
              Upload your existing DDQ (PDF or Word) and we'll use AI to pre-fill as many answers as possible.
            </p>
            {aiMessage && (
              <div style={{
                fontSize: '12px', color: aiProcessing ? '#92640A' : '#0D6E8A',
                background: '#F5F6F8', border: '1px solid #D0D5DD',
                borderRadius: '3px', padding: '10px 14px', marginBottom: '12px'
              }}>
                {aiMessage}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={async () => {
                let currentId = ddqIdRef.current || ddqId;
                if (!currentId && firm) {
                  const { data, error: insertError } = await supabase.from('ddqs').insert({
                    firm_id: firm.id,
                    fund_id: fundId || null,
                    type: ddqType,
                    asset_class: assetClass,
                    status: 'draft',
                    allow_pdf: false,
                  }).select().single();
                  if (insertError) {
                    console.error('Insert error:', insertError);
                    setAiMessage('Insert failed: ' + insertError.message);
                    return;
                  }
                  if (data) {
                    currentId = data.id;
                    setDdqId(data.id);
                    ddqIdRef.current = data.id;
                  }
                }
                if (currentId) {
                  fileInputRef.current?.click();
                } else {
                  setAiMessage('Could not initialise DDQ. Please refresh and try again.');
                }
              }}
              disabled={uploading || aiProcessing}
              style={{
                background: '#0D6E8A15', border: '1px solid #0D6E8A',
                borderRadius: '3px', color: '#0D6E8A', fontSize: '13px',
                padding: '8px 18px', cursor: uploading ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif', opacity: uploading ? 0.7 : 1
              }}
            >
              {uploading ? 'Processing...' : '↑ Upload existing DDQ'}
            </button>
          </div>
        )}

        {/* Section content */}
        {!isLastStep && currentSection && (
          <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: '#0A0A0A', marginBottom: '6px' }}>
                {currentSection.title}
              </h2>
              <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6 }}>{currentSection.description}</p>
            </div>
            {currentSection.questions.map(q => renderQuestion(currentSection, q))}
          </div>
        )}

        {/* Review & Submit */}
        {isLastStep && (
          <div style={{ background: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '4px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: '#0A0A0A', marginBottom: '6px' }}>
              Review & Submit
            </h2>
            <p style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.6, marginBottom: '24px' }}>
              Review your DDQ before submitting. Once submitted it will be available to share with allocators.
            </p>
            {sections.map(s => {
              const sectionAnswers = answers[s.key] || {};
              const answered = Object.values(sectionAnswers).filter(v => v).length;
              const total = s.questions.length;
              return (
                <div key={s.key} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderBottom: '1px solid #E8EBF0', cursor: 'pointer'
                }} onClick={() => setCurrentStep(sections.indexOf(s))}>
                  <span style={{ fontSize: '13px', color: '#0A0A0A' }}>{s.title}</span>
                  <span style={{
                    fontSize: '11px', padding: '2px 10px', borderRadius: '20px',
                    background: answered === total ? '#0A7C4215' : '#92640A15',
                    color: answered === total ? '#0A7C42' : '#92640A'
                  }}>{answered}/{total} answered</span>
                </div>
              );
            })}
            <div style={{ marginTop: '24px', padding: '16px', background: '#F5F6F8', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#0A0A0A', marginBottom: '3px' }}>Allow PDF download</div>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>Let allocators download a watermarked PDF of this DDQ</div>
                </div>
                <button onClick={() => setAllowPdf(!allowPdf)} style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: allowPdf ? '#0A1628' : '#D0D5DD',
                  border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s'
                }}>
                  <div style={{
                    width: '18px', height: '18px', borderRadius: '50%', background: '#FFFFFF',
                    position: 'absolute', top: '3px', left: allowPdf ? '23px' : '3px', transition: 'left 0.2s'
                  }} />
                </button>
              </div>
            </div>
            {status !== 'submitted' ? (
              <button onClick={handleSubmit} disabled={submitting} style={{
                marginTop: '24px', width: '100%', background: '#0A1628',
                border: 'none', borderRadius: '3px', color: '#FFFFFF',
                fontSize: '14px', fontWeight: 600, padding: '14px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif', opacity: submitting ? 0.7 : 1
              }}>{submitting ? 'Submitting...' : 'Submit DDQ'}</button>
            ) : (
              <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '13px', color: '#0A7C42' }}>
                ✓ This DDQ has been submitted
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        {!isLastStep && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              style={{
                background: '#FFFFFF', border: '1px solid #D0D5DD',
                borderRadius: '3px', color: currentStep === 0 ? '#9CA3AF' : '#3D4552',
                padding: '10px 20px', cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                fontSize: '13px', fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >← Previous</button>
            <button
              onClick={() => setCurrentStep(Math.min(sections.length, currentStep + 1))}
              style={{
                background: '#0A1628', border: 'none', borderRadius: '3px',
                color: '#FFFFFF', padding: '10px 24px', cursor: 'pointer',
                fontSize: '13px', fontWeight: 600, fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >{currentStep === sections.length - 1 ? 'Review →' : 'Next →'}</button>
          </div>
        )}
      </div>
    </div>
  );
}